// ═══════════════════════════════════════════════════════════════════════════
// targetTicketPrompt.js — the ONE instruction for reading a handwritten green
// target ticket out of a photograph.
//
// The vocabulary is INTERPOLATED, never baked in: the caller passes the towns
// the board actually bids, the commodities the printed form carries and the
// order structures, so adding a delivery point reaches this prompt on its own.
// That is the same rule basisSheetPrompt follows, and it exists because the
// Avon/Lakeville column has been printed six different ways and every hardcoded
// spelling un-priced somebody's target until an alias caught up.
//
// THE FIELD THIS PROMPT CARES ABOUT MOST IS THE DELIVERY POINT. Five of the ten
// working tickets photographed on 2026-08-24 recorded NO delivery point, and
// that one missing field decides the basis: priced across the points each could
// have meant, it was worth up to 34c — $3,740 on Zeliff's 11,000 bu alone. A
// "+15" written on the ticket does not identify it either, because Batavia,
// LeRoy and Avon were ALL about +0.15 in March and now sit 34c apart. So an
// unreadable delivery point must come back NULL and be asked for, never
// inferred from the basis or from the farm's usual habit.
//
// NOTHING HERE READS A PRICE THE FORM WILL DERIVE. The futures target is
// cash - basis, computed against the live board at save time; asking the model
// for it would invite a transcribed number to overwrite a computed one.
// ═══════════════════════════════════════════════════════════════════════════

function targetTicketPrompt({ towns = [], commodities = [], structures = [] } = {}) {
  const townList = towns.length ? towns.join(", ") : "(none configured)";
  const cropList = commodities.length ? commodities.join(", ") : "(none configured)";
  const structList = structures.length ? structures.join(", ") : "(none configured)";

  return `You are reading a photograph of a handwritten grain TARGET TICKET from a
farm insurance and marketing agency's target book. Return ONLY JSON.

These are pre-printed forms filled in by hand in pen. Two layouts exist and both
appear in the book; read whichever you are given. Handwriting is uneven, fields
are often left blank, and boxes are ticked with an X or a check.

Return this shape exactly:

{
  "farmer_name": string|null,
  "buyer": string|null,
  "entered_on": "YYYY-MM-DD"|null,
  "commodity": string|null,
  "bid_structure": "delivered"|"pickup"|null,
  "delivery_point": string|null,
  "shipment_period": string|null,
  "cme_month": string|null,
  "order_structure": string|null,
  "basis_written": string|null,
  "rungs": [ { "futures_written": number|null, "cash_target": number|null, "bushels": number|null } ],
  "contract_number": string|null,
  "notes": string|null,
  "unreadable": [ string ]
}

RULES — these matter more than filling the form in:

1. WRITE null FOR ANYTHING NOT CLEARLY WRITTEN ON THE TICKET. Do not infer a
   value from another field, from what is typical, or from what would make the
   numbers agree. A null is cheap here: the person is standing in front of the
   screen and will be asked. A guess is expensive and silent.

2. delivery_point MUST be one of these towns, spelled exactly as listed:
   ${townList}
   If the ticket names a place not on that list, put the raw text you see. If NO
   delivery point is written at all, return null and add "delivery point" to
   "unreadable". NEVER infer it from the basis, the buyer or the farm name.

3. bid_structure: "delivered" if a Delivered box is ticked or a town is written
   on the delivered line; "pickup" if a Pickup / FOB Farm / P/U box is ticked.
   null if neither is marked.

4. commodity: one of ${cropList} — whichever box is ticked. null if none is.

5. order_structure: one of ${structList} — whichever box is ticked. If "Basis"
   is ticked, this ticket sits on a BASIS CONTRACT, which matters: it means the
   basis was locked by contract rather than following the board.

6. basis_written: the basis as it is written, in cents, keeping the sign —
   "+15", "-6", "-55". Do NOT convert it to dollars. null if nothing is written.

7. rungs: ONE ENTRY PER PRICE LINE. A ticket often carries several — 10,000 bu
   at $6.25 AND 10,000 bu at $6.50 on the same sheet are two separate orders,
   not one. Read the prices as dollars per bushel ($5.25, $12.25) and bushels as
   a whole number. If a line has a price but no bushels, still return it with
   bushels null. Return [] if no price line is filled in.

8. ⚠ EACH PRICE LINE CARRIES TWO PRICES AND THEY ARE NOT THE SAME NUMBER. At
   the bottom of the form, under the "CBOT" and "Bushels" headings, a line reads

       $12.64  ,  $12.25  Cash   5,000

   The FIRST number, under CBOT, is the FUTURES price -> "futures_written".
   The SECOND, the one the word "Cash" labels, is the CASH price the grower
   agreed to -> "cash_target". They are typically 5c to 60c apart.

   The same futures number is usually written a SECOND time higher up, on the
   "Target @:" line in the Market box — that is the futures order, NOT the cash
   target. A "Price:" line near the top, when it is filled in, is the CASH
   price. So the futures figure commonly appears TWICE on one sheet and the cash
   figure once; do not let the repetition decide it for you.

   Return BOTH numbers whenever both are legible. Return null for either one you
   cannot read — they are checked against each other and the basis afterwards,
   so an honest null is far better than putting the same number in both fields.

9. shipment_period is the delivery window as written ("Oct-Nov", "December",
   "27-Apr", "2027 HARV"). cme_month is the board month ("Z26", "N27"). They are
   DIFFERENT fields and are often both present; do not merge them or copy one
   into the other.

10. "unreadable" lists, in plain words, every field you could not read with
    confidence — "delivery point", "bushels on the second line", "the date".
    This list is read by a human and is the point of the exercise; a short list
    with a wrong value in it is far worse than a longer list of honest gaps.

Return the JSON object and nothing else.`;
}

module.exports = { targetTicketPrompt };
